import React, { useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import { useConfirmPlan, useGetPlan } from "../../../../hooks/queries/plans";
import { useNavigate } from "react-router-dom";
import { Page, Card, Spinner, Banner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../../../components/global/appContext";
import { useQueryClient } from "@tanstack/react-query";

export default function PlanConfirmation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const appContext = useContext(AppContext);
  const queryClient = useQueryClient();
  
  // Fetch fresh plan data immediately on mount and after confirmation
  const { refetch: refetchPlan } = useGetPlan();
  
  // Refetch plan data immediately when component mounts to ensure fresh data
  useEffect(() => {
    refetchPlan();
  }, [refetchPlan]);

  const searchParams = new URLSearchParams(useLocation().search);
  const chargeId = searchParams.get("charge_id");
  const type = searchParams.get("type");
  const statusParam = (searchParams.get("status") || "").toLowerCase();
  const declinedStatuses = ["declined", "cancelled", "canceled", "expired"]; // ensure we gracefully handle variants
  const isDeclined = statusParam && declinedStatuses.includes(statusParam);

  const { isError, error, mutate, isPending } = useConfirmPlan({
    onSuccess: async (data) => {
      const { upgrade } = data;
      // Invalidate cache to ensure no stale data is shown
      queryClient.invalidateQueries(["plan"]);
      // Refetch plan to ensure fresh data is available
      await refetchPlan();
      // Navigate immediately - the Show component will fetch fresh data
      navigate(`/settings/plan?upgrade=${upgrade}`, {
        replace: true,
        target: "APP",
      });
    },
    onError: (mutationError) => {
      const errorMessage =
        mutationError?.response?.data?.error ||
        mutationError?.message ||
        t("settings.plan.confirmation.error");
      appContext?.setMessage?.(errorMessage, true);
    },
  });

  useEffect(() => {
    if (isDeclined) {
      appContext?.setMessage?.(t("settings.plan.confirmation.error"), true);
      navigate("/settings/plan", { replace: true, target: "APP" });
      return;
    }

    if (!chargeId || !type) {
      console.warn("Missing charge_id or type in URL params", { chargeId, type });
      navigate("/settings/plan", { replace: true, target: "APP" });
      return;
    }
    mutate({ chargeId, type });
  }, [chargeId, type, mutate, navigate, isDeclined, appContext, t]);

  if (isError) {
    const errorMessage =
      error?.response?.data?.error ||
      error?.message ||
      t("settings.plan.confirmation.error");
    return (
      <Page>
        <Card sectioned>
          <Banner status="critical" title={t("settings.plan.confirmation.error")}>
            <p>{errorMessage}</p>
          </Banner>
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
        }}
      >
        <Card sectioned>
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <Spinner size="large" />
            <p style={{ marginTop: "1rem" }}>
              {t("settings.plan.confirmation.processing") || "Processing your plan..."}
            </p>
          </div>
        </Card>
      </div>
    </Page>
  );
}
